import "./assistance.css";
import Logo from "../../Admin/IMG/Logo.png";
import SidBarClient from "./SideBarClient";
import { useEffect, useRef, useState } from "react";
import axios from "axios";

interface Message {
  id: number;
  from: "user" | "bot";
  text: string;
  time: string;
  isTicketConfirm?: boolean;
}

type ChatState = "idle" | "ticket_prompt" | "ticket_sent";

const now = () =>
  new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });

const FAQ: { q: string; a: string }[] = [
  {
    q: "Suivi de ma commande",
    a: "Pour suivre votre commande, rendez-vous dans l'onglet **Suivi** de votre espace client. Vous y trouverez l'historique complet de vos commandes et leur statut en temps réel.",
  },
  {
    q: "Problème avec un produit",
    a: "Nous sommes désolés pour ce désagrément. Décrivez votre problème précisément ou placez un ticket pour qu'un agent de votre magasin vous contacte rapidement.",
  },
  {
    q: "Informations de livraison",
    a: "Les délais de livraison dépendent de votre magasin assigné. Consultez votre historique de commande ou contactez directement votre magasin via le ticket ci-dessous.",
  },
  {
    q: "Modifier ma commande",
    a: "Une commande ne peut être modifiée qu'avant sa confirmation. Contactez votre magasin dès que possible en plaçant un ticket ci-dessous.",
  },
];

export default function AssistanceClient() {
  const ClientId   = localStorage.getItem("ClientId");
  const ClientName = localStorage.getItem("ClientName") || "Client";

  const [magasinName, setMagasinName] = useState("Support Magasin");
  const [chatState, setChatState]     = useState<ChatState>("idle");
  const [input, setInput]             = useState("");
  const [isSending, setIsSending]     = useState(false);
  const [messages, setMessages]       = useState<Message[]>([
    {
      id: 1,
      from: "bot",
      text: `Bienvenue ${ClientName} ! 👋 Je suis l'assistant WYN. Comment puis-je vous aider aujourd'hui ?`,
      time: now(),
    },
  ]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ClientId) return;
    axios
      .get(`${process.env.REACT_APP_PHP_APP_URL}/client/${ClientId}/magasin`)
      .then((res) => {
        if (res.data?.NomMagasin) setMagasinName(res.data.NomMagasin);
      })
      .catch(() => {});
  }, [ClientId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const addBot = (text: string, extra?: Partial<Message>) => {
    setMessages((prev) => [
      ...prev,
      { id: Date.now(), from: "bot", text, time: now(), ...extra },
    ]);
  };

  const addUser = (text: string) => {
    setMessages((prev) => [
      ...prev,
      { id: Date.now() + 1, from: "user", text, time: now() },
    ]);
  };

  const handleFAQ = (item: { q: string; a: string }) => {
    addUser(item.q);
    setIsSending(true);
    setTimeout(() => {
      addBot(item.a);
      setIsSending(false);
    }, 800);
  };

  const handleTicketChip = () => {
    addUser("Placer un ticket");
    setTimeout(() => {
      addBot(
        "Bien sûr ! Décrivez votre problème ci-dessous et je créerai automatiquement un ticket pour votre magasin **" +
          magasinName +
          "**."
      );
      setChatState("ticket_prompt");
    }, 600);
  };

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed || isSending) return;
    setInput("");
    addUser(trimmed);
    setIsSending(true);

    if (chatState === "ticket_prompt") {
      try {
        const fd = new FormData();
        fd.append("message", trimmed);
        fd.append("clientName", ClientName);
        fd.append("NamUtilisateur", ClientName);
        await axios.post(
          `${process.env.REACT_APP_PHP_APP_URL}/client/${ClientId}/send-ticket`,
          fd
        );
        setTimeout(() => {
          addBot(
            "✅ Votre ticket a bien été créé ! L'équipe de **" +
              magasinName +
              "** vous répondra dans les plus brefs délais.",
            { isTicketConfirm: true }
          );
          setChatState("ticket_sent");
          setIsSending(false);
        }, 900);
      } catch {
        setTimeout(() => {
          addBot("❌ Une erreur est survenue. Veuillez réessayer ou nous appeler au +212 669-412265.");
          setIsSending(false);
        }, 700);
      }
    } else {
      setTimeout(() => {
        addBot(
          "Merci pour votre message. Un agent de " +
            magasinName +
            " vous répondra bientôt. Vous pouvez également placer un ticket pour un suivi formel."
        );
        setIsSending(false);
      }, 900);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSend();
  };

  const renderText = (text: string) => {
    const parts = text.split(/\*\*(.+?)\*\*/g);
    return parts.map((p, i) =>
      i % 2 === 1 ? <strong key={i}>{p}</strong> : <span key={i}>{p}</span>
    );
  };

  const showChips = messages.length <= 1 && chatState === "idle";

  return (
    <div className="asst-page-wrapper">
      <SidBarClient />
      <div className="asst-container">
        {/* Title bar */}
        <div className="asst-title-bar">
          <button onClick={() => window.history.back()} className="asst-back-btn">
            ←
          </button>
          <div>
            <h4>Assistance WYN</h4>
            <p>Chat support — {magasinName}</p>
          </div>
        </div>

        {/* Chat card */}
        <div className="asst-chat-card">
          {/* Header */}
          <div className="asst-chat-header">
            <div className="asst-header-profile">
              <div className="asst-header-avatar">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1a1a2e" strokeWidth="2.5">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
              </div>
              <div className="asst-header-info">
                <h5>{magasinName}</h5>
                <span className="asst-status-online">
                  <span className="asst-status-dot" />
                  En ligne
                </span>
              </div>
            </div>
            <div className="asst-header-phone">+212 669-412265</div>
          </div>

          {/* Messages */}
          <div className="asst-messages-area">
            {messages.map((msg) =>
              msg.from === "bot" ? (
                <div key={msg.id} className="asst-msg-row">
                  <img
                    src={Logo}
                    alt="WYN"
                    className="asst-msg-avatar"
                  />
                  <div>
                    <div
                      className={`asst-msg-bubble ${msg.isTicketConfirm ? "asst-msg-bubble--ticket" : ""}`}
                    >
                      {renderText(msg.text)}
                    </div>
                    <div className="asst-msg-time">{msg.time}</div>
                  </div>
                </div>
              ) : (
                <div key={msg.id} className="asst-msg-row asst-msg-row--user">
                  <div>
                    <div className="asst-msg-bubble asst-msg-bubble--user">{msg.text}</div>
                    <div className="asst-msg-time">{msg.time}</div>
                  </div>
                </div>
              )
            )}

            {/* Initial quick-action chips */}
            {showChips && (
              <div className="asst-chips-row">
                {FAQ.map((item) => (
                  <button
                    key={item.q}
                    onClick={() => handleFAQ(item)}
                    className="asst-chip"
                  >
                    {item.q}
                  </button>
                ))}
                <button
                  onClick={handleTicketChip}
                  className="asst-chip asst-chip--ticket"
                >
                  🎫 Placer un ticket
                </button>
              </div>
            )}

            {/* After first exchange, show "Placer un ticket" chip if not yet in ticket mode */}
            {!showChips && chatState === "idle" && messages.length >= 2 && messages.length < 8 && (
              <div className="asst-chips-row">
                {FAQ.slice(0, 2).map((item) => (
                  <button
                    key={item.q}
                    onClick={() => handleFAQ(item)}
                    className="asst-chip"
                    style={{ borderColor: "#e2e8f0", color: "#64748b" }}
                  >
                    {item.q}
                  </button>
                ))}
                <button
                  onClick={handleTicketChip}
                  className="asst-chip asst-chip--ticket"
                >
                  🎫 Placer un ticket
                </button>
              </div>
            )}

            {/* After ticket sent: show "New question" chip */}
            {chatState === "ticket_sent" && (
              <div className="asst-chips-row">
                <button
                  onClick={() => { setChatState("idle"); }}
                  className="asst-chip"
                >
                  Autre question
                </button>
              </div>
            )}

            {/* Typing indicator */}
            {isSending && (
              <div className="asst-msg-row">
                <img src={Logo} alt="WYN" className="asst-msg-avatar" />
                <div className="asst-msg-bubble" style={{ display: "flex", gap: "5px", alignItems: "center" }}>
                  <span className="asst-typing-dot" style={{ animationDelay: "0s" }} />
                  <span className="asst-typing-dot" style={{ animationDelay: "0.2s" }} />
                  <span className="asst-typing-dot" style={{ animationDelay: "0.4s" }} />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input bar */}
          <div className="asst-input-section">
            {chatState === "ticket_prompt" && (
              <div className="asst-ticket-banner">
                🎫 Mode ticket — votre prochain message créera un ticket pour {magasinName}
              </div>
            )}
            <div
              className={`asst-input-wrapper ${chatState === "ticket_prompt" ? "asst-input-wrapper--ticket" : ""}`}
            >
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={
                  chatState === "ticket_prompt"
                    ? "Décrivez votre problème... (Entrée pour envoyer le ticket)"
                    : "Écrivez votre message..."
                }
                className="asst-input-field"
              />
              <button
                onClick={handleSend}
                disabled={!input.trim() || isSending}
                className={`asst-send-btn ${input.trim() ? "asst-send-btn--active" : ""}`}
              >
                <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}